'use client';

import type { NavigationItem } from '@dealerportal/common';
import { getNavParentAsChild } from '@dealerportal/utilities';
import classNames from 'classnames';
import { useContext, useMemo, useState } from 'react';
import { NavMobileMenuItemLink } from './NavMobileMenuItemLink';
import { NavigationContext } from './Navigation';

const NavMobileDropDown = ({
  menu,
  depthLevel,
  onClickHandler,
}: {
  menu: NavigationItem;
  depthLevel: number;
  onClickHandler: (item: NavigationItem) => void;
}) => {
  const [showSubMenu, setSubMenu] = useState(false);
  const handleMenuDisplay = () => setSubMenu(!showSubMenu);

  const navClassNames = useContext(NavigationContext);

  const adjustedDepthLevel = depthLevel + 1;
  const menuChildren = useMemo(() => {
    const navParentItem = getNavParentAsChild({ item: menu, mode: 'mobile' });
    return menu?.children?.length
      ? [navParentItem satisfies NavigationItem, ...menu.children]
      : null;
  }, [menu?.children]);
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {menu.children && menu.hasVisibleChildren ? (
        <li
          className={`nav__item ui-m-0 ${
            menu.children && menu.hasVisibleChildren
              ? '--has-children'
              : '--no-children'
          } ${navClassNames.itemclass}`}
          data-mobile="true"
        >
          <div
            onClick={handleMenuDisplay}
            aria-hidden="true"
            className={`nav__link ui-flex ui-items-center ui-justify-between ui-p-4 ui-cursor-pointer ui-text-base ${navClassNames.linkclass}`}
          >
            {menu.name}
            {menu.children && menu.hasVisibleChildren && (
              <svg
                className={classNames('ui-w-2', {
                  'ui-rotate-90': showSubMenu,
                })}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
              >
                <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
              </svg>
            )}
          </div>

          <ul
            className={classNames(
              `nav__child-list !ui-m-0 ui-bg-gray-100 !ui-list-none ${navClassNames.childlistclass}`,
              {
                'ui-block': showSubMenu,
                'ui-hidden': !showSubMenu,
              }
            )}
          >
            {menuChildren &&
              (menuChildren as NavigationItem[])
                .filter(({ hideInMenu, isVisible }) => !hideInMenu && isVisible)
                .map((item) => {
                  return item.children && item.hasVisibleChildren ? (
                    <li
                      className={`nav__child-item ui-m-0 ${navClassNames.childitemclass} --has-children`}
                      key={item._id}
                    >
                      <div
                        onClick={() => onClickHandler(item)}
                        aria-hidden="true"
                        className={`nav__child-link ui-flex ui-items-center ui-justify-between ui-px-8 ui-py-4 ui-cursor-pointer ui-text-base ${navClassNames.childlinkclass}`}
                      >
                        {item.name}
                        <svg
                          className="ui-w-2"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 320 512"
                        >
                          <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
                        </svg>
                      </div>
                    </li>
                  ) : (
                    <NavMobileMenuItemLink
                      depthLevel={adjustedDepthLevel}
                      key={item.name}
                      menu={item}
                      className="ui-px-8"
                    />
                  );
                })}
          </ul>
        </li>
      ) : (
        <NavMobileMenuItemLink depthLevel={adjustedDepthLevel} menu={menu} />
      )}
    </>
  );
};

export default NavMobileDropDown;
